import { Modal } from "@tyrannosaurustech/ui";
import { useState } from "react";
import styled from "styled-components";
import { usePushNotesToConnectedMutation } from "../../../redux/reduxQuery/providerNotesApi";
import useToast from "../../../hooks/useToast";
import { Theme } from "../../../components/atoms/theme";
import { ParagraphS, SubtitlesS } from "../../../components/atoms/Typography";
import { TextArea } from "../../../components/atoms/TextArea";

const FormGroup = styled.div`
  margin-bottom: 1rem;
`;

const Label = styled(SubtitlesS)`
  margin-bottom: 0.5rem;
  display: block;
`;

const Select = styled.select`
  width: 100%;
  padding: 0.5rem;
  border: 1px solid ${Theme.colors.neutrals_300};
  border-radius: 4px;
  font-size: 14px;
  &:focus {
    outline: none;
    border-color: ${Theme.colors.primaries_000};
  }
`;

interface MassNotePushModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  provider: { id: string; name: string };
  connectionsCount: number;
  refetchFunction: () => void;
}

const noteFieldOptions = [
  { value: "sender", label: "Sender Notes" },
  { value: "followUp", label: "Follow Up Notes" },
  { value: "vetting", label: "Vetting Notes" },
];

export const MassNotePushModal = ({
  open,
  setOpen,
  provider,
  connectionsCount,
  refetchFunction,
}: MassNotePushModalProps) => {
  const [noteType, setNoteType] =
    useState<"sender" | "followUp" | "vetting">("sender");
  const [noteContent, setNoteContent] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [pushNotesToConnected] = usePushNotesToConnectedMutation();
  const toast = useToast();

  const handleSubmit = async () => {
    if (!noteContent.trim()) {
      toast.warn("Please enter note content");
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await pushNotesToConnected({
        providerId: provider.id,
        noteType,
        noteContent: noteContent.trim(),
      }).unwrap();

      if (result.failed.length > 0) {
        toast.warn(
          `Created notes on ${result.created} providers, ${result.failed.length} failed`
        );
      } else {
        toast.success(`Note pushed to ${result.created} connected providers`);
      }

      refetchFunction();
      handleClose();
    } catch (error) {
      toast.error("Failed to push note to connected providers");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setNoteType("sender");
    setNoteContent("");
    setOpen(false);
  };

  return (
    <Modal
      open={open}
      setOpen={(shouldOpen) => {
        if (!shouldOpen) {
          handleClose();
        }
      }}
      loading={isSubmitting}
      title="Push Note to Connected Providers"
      confirmText={`Push to ${connectionsCount} Provider${
        connectionsCount !== 1 ? "s" : ""
      }`}
      onConfirm={handleSubmit}
      noConfirm={!noteContent.trim()}
    >
      <div style={{ width: "100%" }}>
        <ParagraphS
          color={Theme.colors.gray_200}
          style={{ marginBottom: "1rem" }}
        >
          Push a note to all providers connected to{" "}
          <strong>{provider.name}</strong>. This will create a new note record
          on {connectionsCount} connected provider
          {connectionsCount !== 1 ? "s" : ""}.
        </ParagraphS>

        <FormGroup>
          <Label>Note Type</Label>
          <Select
            value={noteType}
            onChange={(e) =>
              setNoteType(e.target.value as "sender" | "followUp" | "vetting")
            }
          >
            {noteFieldOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </FormGroup>

        <FormGroup>
          <Label>Note Content</Label>
          <TextArea
            value={noteContent}
            onChange={(e) => setNoteContent(e.target.value)}
            placeholder="Enter the note to push to connected providers..."
            width="100%"
            lineHeight="1.5"
          />
        </FormGroup>

        <ParagraphS color={Theme.colors.gray_200} style={{ fontSize: "12px" }}>
          Each connected provider will receive a new note record with this
          content.
        </ParagraphS>
      </div>
    </Modal>
  );
};
